import React, { useEffect, useState } from 'react';
import axios from 'axios';
import adminAxios from './adminApi';
import { Sparkles, Loader2, Copy, Check, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATES = [
  { id: 'thanksgiving', label: 'Thanksgiving', tone: '🦃 Orange / amber, autumn vibes', defaultMsg: "We're grateful for your trust and partnership. Wishing you a warm Thanksgiving filled with joy and gratitude." },
  { id: 'christmas',    label: 'Christmas',    tone: '🎄 Red / green, festive',          defaultMsg: "Wishing you and your family a Merry Christmas filled with warmth, joy, and time with loved ones." },
  { id: 'newyear',      label: 'New Year',     tone: '✨ Navy / gold, celebratory',       defaultMsg: "Thank you for an incredible year. Wishing you a healthy, prosperous and happy New Year." },
  { id: 'july4',        label: 'July 4th',     tone: '🇺🇸 Red / white / blue, patriotic', defaultMsg: "Happy Independence Day! Wishing you a safe and joyful 4th of July." },
  { id: 'generic',      label: 'General Thanks', tone: '💙 Brand blue, evergreen',         defaultMsg: "Thank you for being a valued customer. We appreciate your business and look forward to serving you." },
];

const GreetingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState('thanksgiving');
  const [customMessage, setCustomMessage] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/greeting`)
      .then((r) => {
        setTemplate(r.data.template || 'thanksgiving');
        setCustomMessage(r.data.custom_message || '');
        setEnabled(r.data.enabled !== false);
        setUpdatedAt(r.data.updated_at);
      })
      .catch(() => setError('Could not load current greeting'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const { data } = await adminAxios.put('/admin/greeting', {
        template,
        custom_message: customMessage || null,
        enabled,
      });
      setUpdatedAt(data.updated_at);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const shareUrl = `${window.location.origin}/greetings`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // noop
    }
  };

  const activeTemplate = TEMPLATES.find((t) => t.id === template) || TEMPLATES[0];

  if (loading) {
    return <div className="bg-white rounded-xl p-12 text-center"><Loader2 className="animate-spin mx-auto text-blue-600" size={28} /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><Sparkles size={18} className="text-amber-500" /> Seasonal Greetings</h2>
          <p className="text-sm text-gray-500 mt-1">Pick a holiday template, customise the message, and share one link with your customers.</p>
        </div>
        {updatedAt && (
          <p className="text-xs text-gray-400">Last saved {new Date(updatedAt).toLocaleString()}</p>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        <div>
          <label className="block text-sm font-semibold mb-2">Template</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {TEMPLATES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => { setTemplate(t.id); if (!customMessage) setCustomMessage(t.defaultMsg); }}
                data-testid={`greeting-template-${t.id}`}
                className={`text-left p-3 rounded-lg border-2 transition ${template === t.id ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <div className="font-semibold text-sm">{t.label}</div>
                <div className="text-xs text-gray-500 mt-1">{t.tone}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">Custom message <span className="font-normal text-gray-400">(optional — leave blank to use the default)</span></label>
          <textarea
            rows={4}
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder={activeTemplate.defaultMsg}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            data-testid="greeting-message-input"
          />
        </div>

        <div className="flex items-center gap-2">
          <input type="checkbox" id="greeting-enabled" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="greeting-enabled-toggle" />
          <label htmlFor="greeting-enabled" className="text-sm font-semibold">Greeting page is live (visible at the share link)</label>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2"
            data-testid="greeting-save-button"
          >
            {saving ? <><Loader2 className="animate-spin" size={16} /> Saving...</> : 'Save greeting'}
          </button>
        </div>
      </div>

      <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-blue-100 p-6">
        <h3 className="font-bold text-gray-900 mb-2">Share with your customers</h3>
        <p className="text-sm text-gray-600 mb-4">Send this link to your customers — it always shows the currently saved greeting.</p>
        <div className="flex items-center gap-2 flex-wrap">
          <input readOnly value={shareUrl} className="flex-1 min-w-0 bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" data-testid="greeting-share-url" />
          <button onClick={handleCopy} className="flex items-center gap-2 bg-gray-900 hover:bg-black text-white px-4 py-2 rounded-lg text-sm font-semibold" data-testid="greeting-copy-button">
            {copied ? <><Check size={16} /> Copied!</> : <><Copy size={16} /> Copy</>}
          </button>
          <a href="/greetings" target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-semibold">
            <ExternalLink size={16} /> Preview
          </a>
        </div>
      </div>
    </div>
  );
};

export default GreetingsTab;
